import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import {
  API_BASE_URL,
  AUTH_ENDPOINTS,
  LEAD_ENDPOINTS,
  AGENT_ENDPOINTS,
  ADMIN_AGENT_ENDPOINTS,
  PROVIDER_LATENCY_ENDPOINTS,
  AGENT_EVALUATION_ENDPOINTS,
  LANGUAGE_ENDPOINTS,
  AUDIO_RECORDINGS_ENDPOINTS,
  CONVERSATIONS_ENDPOINTS,
  TAG_ENDPOINTS,
  CAMPAIGN_ENDPOINTS,
} from "../config/api";
import { handleApiError } from "../utils/apiErrorHandler";
import { StrategyProviders, Strategy } from "../types/strategies";
import { StrategyConfigDraft } from "../types/strategyConfig";

// Create API instance with base URL and credentials
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});

// Add token to all requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// Handle unauthorized responses and token expiration
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Update token timestamp on successful responses that require authentication
    if (response.config.headers?.Authorization) {
      localStorage.setItem("tokenTimestamp", Date.now().toString());
    }
    return response;
  },
  (error: AxiosError) => {
    // Handle 5xx server errors with the error handler
    if (
      error.response &&
      error.response.status >= 500 &&
      error.response.status <= 599
    ) {
      const handled = handleApiError(error);
      if (handled) {
        return Promise.reject(new Error("API error handled with fallback UI"));
      }
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Make this exmpemt if coming from the audio agent
      if (window.location.pathname.includes("/agent")) {
        return Promise.reject(error);
      }
      console.log("Received 401 Unauthorized response - logging out user");
      // Clear auth data
      localStorage.removeItem("token");
      localStorage.removeItem("adminToken");
      localStorage.removeItem("userType");
      localStorage.removeItem("tokenTimestamp");
      localStorage.removeItem("remainingUses");

      // Redirect to login page if not already there
      const currentPath = window.location.pathname;
      if (
        !currentPath.includes("/login") &&
        !currentPath.includes("/admin/login")
      ) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to get error message
const getErrorMessage = (error: any): string => {
  const detail = error.response?.data?.detail;

  // Handle FastAPI validation errors (422) which return an array of error objects
  if (Array.isArray(detail)) {
    return detail
      .map((err: any) => {
        const loc = err.loc?.join(' -> ') || 'unknown field';
        const msg = err.msg || 'validation error';
        return `${loc}: ${msg}`;
      })
      .join('; ');
  }

  // Handle object detail (shouldn't happen but just in case)
  if (detail && typeof detail === 'object') {
    return JSON.stringify(detail);
  }

  return detail || error.message || "An error occurred";
};

export class AgentEvaluationUnavailableError extends Error {
  constructor() {
    super("Agent evaluation backend is not available yet.");
    this.name = "AgentEvaluationUnavailableError";
  }
}

const throwAgentEvaluationError = (
  error: any,
  options: { unavailableOn404?: boolean } = {}
): never => {
  if (options.unavailableOn404 && error?.response?.status === 404) {
    throw new AgentEvaluationUnavailableError();
  }

  throw new Error(getErrorMessage(error));
};

// Lead Login response interface
export interface LeadLoginResponse {
  access_token: string;
  token_type: string;
  remaining_uses?: number;
}

// Lead interfaces (renamed from OTP)
export interface Lead {
  id: number;
  code: string;
  max_uses: number;
  remaining_uses: number;
  expires_at: string;
  fullname: string;
  email: string;
  phone: string;
  company: string;
  conversations: Conversation[];
  agents?: LeadAgentAccess[];
}

// New interface for Lead Agent Access
export interface LeadAgentAccess {
  id: number;
  uuid: string;
  name: string;
  description: string;
  is_outbound: boolean;
  access_token: string | null;
}

export interface LeadSignup {
  max_uses: number;
  fullname: string;
  email: string;
  phone: string;
  company: string;
}

export interface LeadListResponse {
  data: Lead[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
  remaining_uses: number | null;
}

// Conversation interfaces
export interface ChatMessage {
  role: string;
  content: string;
}

export interface AgentInfo {
  id: number;
  name: string;
  description: string;
  voice_message?: string;
  enabled: boolean;
  live?: boolean;
  is_outbound: boolean;
  limitations: string[];
  tags: string[];
  is_public: boolean;
}

export interface Conversation {
  lead_id: number | null;
  agent_id: number;
  agent?: AgentInfo;
  agent_name?: string;
  timestamp: string;
  recording_filename?: string;
  chat_history?: ChatMessage[];
  is_s3: boolean;
  id?: number;
  pinned?: boolean;
  call_number?: string;
}

export interface ConversationsResponse {
  data: Conversation[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// Tag interfaces
export interface Tag {
  id: number;
  name: string;
  description: string;
}

// Language interfaces
export interface Language {
  id: number;
  language: string;
  language_code: string;
  google_voice_id: string;
}

export interface LanguageMapping {
  language: string;
  language_code: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
  remaining_uses?: number;
}

// Success Metrics Interfaces
export interface SuccessMetric {
  id?: string;
  question: string;
  slug: string;
  answer_type: AnswerType;
  category: MetricCategory;
  priority: MetricPriority;
  reason: string;
  chart_type: ChartType;
  extraction_hints?: string;
  options?: string[];
  config?: Record<string, any>;
}

export enum AnswerType {
  YES_NO = "yes-no",
  TRUE_FALSE = "true-false",
  PRESENT_ABSENT = "present-absent",
  SINGLE_SELECT = "single-select",
  MULTI_SELECT = "multi-select",
  SCALE_5 = "scale-5",
  SCALE_10 = "scale-10",
  NPS = "nps",
  SENTIMENT = "sentiment",
  NUMBER = "number",
  CURRENCY = "currency",
  PERCENTAGE = "percentage",
  DURATION_IN_MINUTES = "duration-in-minutes",
  TEXT_SHORT = "text-short",
  TEXT_LONG = "text-long"
}

export enum ChartType {
  PIE = "pie",
  BAR = "bar",
  GAUGE = "gauge",
  LINE = "line",
  METRIC = "metric",
  FUNNEL = "funnel"
}

export enum MetricPriority {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low"
}

export enum MetricCategory {
  SETUP = "Setup",
  ENGAGEMENT = "Engagement",
  RESOLUTION = "Resolution",
  OUTCOME = "Outcome",
  SATISFACTION = "Satisfaction",
  FOLLOW_UP = "Follow-up"
}

// Campaign Analytics Interfaces
export interface AnalysisMetadata {
  generated_at: string;
  analysis_version: string;
  data_freshness: string;
  applied_filters: {
    date_range: boolean;
    metric_priorities: string | null;
    call_status: string | null;
    metric_categories: string | null;
  };
}

export interface CallSummary {
  total_calls: number;
  analyzed_calls: number;
  failed_analyses: number;
  pending_analyses: number;
  analysis_success_rate: number;
  avg_call_duration: number;
  total_analysis_time: number;
}

export interface AggregatedData {
  type: 'binary' | 'scale' | 'single_select' | 'multi_select';
  distribution: Record<string, number>;
  percentages: Record<string, number>;
  average_score?: number;
  percentiles?: {
    p25: number;
    p50: number;
    p75: number;
  };
  top_response?: {
    option: string;
    count: number;
    percentage: number;
  };
  total_selections?: number;
  avg_selections_per_response?: number;
}

export interface ChartData {
  pie?: {
    labels: string[];
    values: number[];
    colors: string[] | null;
  };
  bar?: {
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
    }>;
  };
  gauge?: any;
  metric?: any;
}

export interface CampaignSuccessMetric {
  id: string;
  question: string;
  slug: string;
  answer_type: string;
  chart_type: string;
  category: string;
  priority: string;
  reason: string;
  extraction_hints: string;
  aggregated_data: AggregatedData;
  chart_data: ChartData;
}

export interface CategoryPerformance {
  total_metrics: number;
  avg_completion: number;
  avg_confidence: number;
}

export interface Insights {
  completion_rate_by_priority: {
    high: number;
    medium: number;
    low: number;
  };
  category_performance: Record<string, CategoryPerformance>;
  data_quality_summary: {
    overall_quality: string;
    avg_confidence: number;
    completeness_rate: number;
    quality_issues: string[];
  };
}

export interface CampaignAnalyticsResponse {
  analysis_metadata: AnalysisMetadata;
  call_summary: CallSummary;
  success_metrics: CampaignSuccessMetric[];
  insights: Insights;
  performance: {
    query_duration_ms: number;
    cache_hit: boolean;
    total_records_processed: number;
  };
}

// Agent Management Interfaces
export type AgentUserDetails = Record<string, string>;
export type AgentRuntimeFields = Record<
  string,
  string | AgentUserDetails | string[]
>;

export interface AgentSummary {
  uuid: string;
  name: string;
  description: string;
  enabled: boolean;
  is_outbound: boolean;
  primary_language: string;
  supported_languages: string[];
  tags: string[];
  limitations: string[];
  is_public?: boolean;
  phone_supported?: boolean;
  can_disconnect?: boolean;
  access_token?: string;
  system_prompt?: string;
  system_prompt_token_count?: number;
  phone_number?: string | null;
  suggested_metrics?: SuccessMetric[];
  selected_metrics?: SuccessMetric[];
  fields?: AgentRuntimeFields;
}

export type ProviderFieldType =
  | "text"
  | "textarea"
  | "select"
  | "toggle"
  | "number"
  | "range";

export interface ProviderFieldOption {
  label: string;
  value: string;
}

export interface ProviderFieldDefinition {
  name: string;
  label: string;
  type: ProviderFieldType;
  required?: boolean;
  default?: string | number | boolean;
  options?: ProviderFieldOption[];
  min?: number;
  max?: number;
  step?: number;
  helpText?: string;
  placeholder?: string;
  mapToAgentField?: string;
  show_for_models?: string[];
  model_overrides?: Record<string, Partial<ProviderFieldDefinition>>;
}

export interface ProviderDefinition {
  id: string;
  label: string;
  description?: string;
  fields: ProviderFieldDefinition[];
}

export interface ProviderManifest {
  stt: ProviderDefinition[];
  llm: ProviderDefinition[];
  tts: ProviderDefinition[];
  v2v: ProviderDefinition[];
}

export interface AgentEvaluationProviderSelection {
  provider: string;
  settings: Record<string, any>;
}

export type AgentEvaluationPersonaType = "good" | "bad" | "edge";
export type AgentEvaluationCallDirection = "incoming" | "outbound";

export type AgentEvaluationStatus =
  | "draft"
  | "queued"
  | "running"
  | "passed"
  | "failed"
  | "needs_review"
  | "cancelled";

export type AgentEvaluationRunType = "baseline" | "candidate";
export type AgentEvaluationVerdict = "pass" | "fail" | "needs_review";
export type AgentPromptCandidateStatus =
  | "draft"
  | "running"
  | "completed"
  | "accepted"
  | "rejected";

export interface AgentEvaluationCriteriaResult {
  criterion: string;
  passed: boolean;
  notes?: string;
}

export interface AgentEvaluationScenarioResult {
  id: string;
  run_id: string;
  scenario_id: string;
  status: AgentEvaluationStatus;
  verdict: AgentEvaluationVerdict;
  score?: number | null;
  turns: ChatMessage[];
  criteria_results: AgentEvaluationCriteriaResult[];
  failure_reason: string;
  fixes_needed: string[];
  judge_notes: string;
  critical_failure: boolean;
  created_at: string;
}

export interface AgentEvaluationScenario {
  id: string;
  title: string;
  persona_name: string;
  persona_type: AgentEvaluationPersonaType;
  languages: string[];
  language_switching: boolean;
  call_direction: AgentEvaluationCallDirection;
  user_goal: string;
  starting_message: string;
  expected_behavior: string;
  pass_criteria: string[];
  failure_traps: string[];
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  latest_result?: AgentEvaluationScenarioResult | null;
}

export type AgentEvaluationScenarioPayload = Omit<
  AgentEvaluationScenario,
  "id" | "version" | "is_active" | "created_at" | "updated_at" | "latest_result"
>;

export interface AgentEvaluationRun {
  id: string;
  run_type: AgentEvaluationRunType;
  status: AgentEvaluationStatus;
  prompt_candidate_id?: string | null;
  scenario_ids: string[];
  user_provider: Record<string, any>;
  judge_provider: Record<string, any>;
  pass_threshold: number;
  max_turns: number;
  accuracy_score?: number | null;
  summary: Record<string, any>;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  results: AgentEvaluationScenarioResult[];
}

export interface AgentEvaluationPromptCandidate {
  id: string;
  source_run_id?: string | null;
  baseline_run_id?: string | null;
  comparison_run_id?: string | null;
  status: AgentPromptCandidateStatus;
  current_prompt: string;
  proposed_prompt: string;
  source: string;
  iteration_count: number;
  baseline_accuracy?: number | null;
  candidate_accuracy?: number | null;
  fixed_scenarios: string[];
  remaining_failures: string[];
  new_regressions: string[];
  critical_failures: string[];
  risk_notes: string;
  created_at: string;
  updated_at: string;
  accepted_at?: string | null;
}

export interface AgentPromptVersion {
  id: string;
  prompt: string;
  source: string;
  candidate_id?: string | null;
  created_at: string;
}

export interface AgentEvaluationSuite {
  agent_uuid: string;
  current_prompt: string;
  scenarios: AgentEvaluationScenario[];
  runs: AgentEvaluationRun[];
  prompt_candidates: AgentEvaluationPromptCandidate[];
  prompt_versions: AgentPromptVersion[];
}

export interface AgentEvaluationGenerateScenariosRequest {
  provider: AgentEvaluationProviderSelection;
  persona_count: number;
  test_cases_per_persona: number;
  max_parallel_generations: number;
  persona_mix: AgentEvaluationPersonaType[];
  languages: string[];
  include_language_switching: boolean;
  call_direction: AgentEvaluationCallDirection;
  instructions?: string;
}

export interface AgentEvaluationGenerateScenariosResponse {
  scenarios: AgentEvaluationScenario[];
  requested_count: number;
  generated_count: number;
  failed_count: number;
  failed_slots: string[];
}

export interface AgentEvaluationRunRequest {
  agent_provider?: AgentEvaluationProviderSelection;
  user_provider: AgentEvaluationProviderSelection;
  judge_provider: AgentEvaluationProviderSelection;
  scenario_ids: string[];
  scenario_definitions?: Array<
    AgentEvaluationScenarioPayload & { id: string; version?: number }
  >;
  max_turns: number;
  max_parallel_scenarios: number;
  pass_threshold: number;
  runtime_fields?: AgentRuntimeFields;
  prompt_candidate_id?: string;
  prompt_override?: string;
}

export interface AgentEvaluationPromptOptimizationRequest {
  source_run_id: string;
  optimizer_provider: AgentEvaluationProviderSelection;
  agent_provider?: AgentEvaluationProviderSelection;
  user_provider: AgentEvaluationProviderSelection;
  judge_provider: AgentEvaluationProviderSelection;
  max_iterations: number;
  max_parallel_scenarios: number;
  pass_threshold: number;
  runtime_fields?: AgentRuntimeFields;
}

export interface AgentEvaluationManualPromptCandidateRequest {
  source_run_id: string;
  proposed_prompt: string;
}

export interface AgentEvaluationPromptCandidateUpdate {
  proposed_prompt: string;
  agent_provider?: AgentEvaluationProviderSelection;
  user_provider?: AgentEvaluationProviderSelection;
  judge_provider?: AgentEvaluationProviderSelection;
  max_parallel_scenarios?: number;
  pass_threshold?: number;
  runtime_fields?: AgentRuntimeFields;
  rerun?: boolean;
}

export interface AgentCreate {
  name: string;
  description: string;
  startup_message: string;
  voice_message?: string;
  system_prompt?: string;
  enabled?: boolean;
  live?: boolean;
  is_outbound?: boolean;
  is_public?: boolean;
  temperature?: number;
  primary_language_id: number;
  supported_language_ids?: number[];
  limitations?: string[];
  tag_ids?: number[];
  can_disconnect?: boolean;
  phone_number?: string;
  pstn_provider?: string;
  model_name?: string;
  access_token?: string;
  proactive_audio?: boolean;
  enable_affective_dialog?: boolean;
  strategy: Strategy | null;
  strategy_providers: StrategyProviders | null;
  strategy_config?: StrategyConfigDraft | null;
  latency_comparison_providers?: Partial<Record<"stt" | "llm" | "tts" | "v2v", string[]>>;
  latency_alerts?: {
    enabled?: boolean;
    email_recipients?: string[];
    slack_email_recipients?: string[];
  };
}

export interface AgentDetail extends Omit<AgentCreate, "tag_ids"> {
  uuid: string;
  tags: Tag[];
  fields?: AgentRuntimeFields;
}

export interface AgentAccessResponse {
  remaining_uses: number | null;
  agent_info: AgentSummary;
}

export interface LanguageCodes {
  [key: string]: string;
}

export interface VoiceConfig {
  [key: string]: {
    language_code: string;
    voice_name: string;
  };
}

export const getLanguageCodes = async (): Promise<Record<string, string>> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.get(LANGUAGE_ENDPOINTS.MAPPINGS, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.reduce(
      (acc: Record<string, string>, item: LanguageMapping) => {
        acc[item.language_code] = item.language;
        return acc;
      },
      {}
    );
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getVoiceConfig = async (): Promise<Record<string, any>> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.get("/admin/agents/voice-config", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Language management functions
export const getLanguageMappings = async (): Promise<LanguageMapping[]> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.get(LANGUAGE_ENDPOINTS.MAPPINGS, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getAllLanguages = async (): Promise<Language[]> => {
  try {
    const response = await api.get(LANGUAGE_ENDPOINTS.LIST, {
      headers: getAuthenticatedHeaders(),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const createLanguage = async (language: {
  language: string;
  language_code: string;
  google_voice_id: string;
}): Promise<Language> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(LANGUAGE_ENDPOINTS.CREATE, language, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateLanguage = async (
  id: number,
  language: {
    language: string;
    language_code: string;
    google_voice_id: string;
  }
): Promise<Language> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(LANGUAGE_ENDPOINTS.UPDATE(id), language, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteLanguage = async (id: number): Promise<void> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    await api.delete(LANGUAGE_ENDPOINTS.DELETE(id), {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Change admin password
export const changeAdminPassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(
      AUTH_ENDPOINTS.CHANGE_PASSWORD,
      {
        current_password: currentPassword,
        new_password: newPassword,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return {
      success: true,
      message: response.data.message || "Password changed successfully",
    };
  } catch (error: any) {
    console.error("Error changing admin password:", error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Fetch a list of agents for clients to access
export const fetchAgentList = async (
  limit = 10,
  offset = 0,
  search?: string
): Promise<PaginatedResponse<AgentSummary>> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Not authenticated");
    }

    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    if (search && search.trim() !== "") {
      params.set("search", search.trim());
    }

    const response = await api.get(
      `${AGENT_ENDPOINTS.LIST}?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Re-export necessary functions with new names to maintain backward compatibility
export const fetchAllAgents = fetchAgentList;
export const createAgent = async (
  agentData: AgentCreate
): Promise<AgentDetail> => {
  // Filter out deprecated fields from the agent data
  const {
    name,
    description,
    startup_message,
    voice_message,
    system_prompt,
    enabled,
    live,
    is_outbound,
    temperature,
    primary_language_id,
    supported_language_ids,
    limitations,
    tag_ids,
    phone_number,
    model_name,
    access_token,
    is_public,
    can_disconnect,
    proactive_audio,
    enable_affective_dialog,
    strategy,
    strategy_providers,
    strategy_config,
    latency_alerts,
  } = agentData as any;

  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(
      ADMIN_AGENT_ENDPOINTS.CREATE,
      {
        name,
        description,
        startup_message,
        voice_message,
        system_prompt,
        enabled,
        live,
        is_outbound,
        temperature,
        primary_language_id,
        supported_language_ids,
        limitations,
        tag_ids,
        phone_number,
        model_name,
        access_token,
        is_public,
        can_disconnect,
        proactive_audio,
        enable_affective_dialog,
        strategy,
        strategy_providers,
        strategy_config,
        latency_alerts,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Agent creation failed:", error);
    console.error("Error details:", error.response?.data || error.message);
    throw new Error(getErrorMessage(error));
  }
};

export interface ProviderLatencyProbeTarget {
  provider: string;
  stage: string;
  threshold_ms?: number | null;
  enabled?: boolean;
  agents?: Array<{
    id?: number | null;
    uuid?: string | null;
    name?: string | null;
    latency_alerts?: {
      enabled?: boolean;
      email_recipients?: string[];
      slack_email_recipients?: string[];
    };
    usage?: "current" | "compare";
  }>;
}

export interface ProviderLatencyMonitorConfig {
  id?: number | null;
  enabled: boolean;
  interval_minutes: number;
  timeout_seconds: number;
  alert_cooldown_minutes: number;
  slack_enabled: boolean;
  slack_channel?: string | null;
  email_enabled: boolean;
  email_recipients: string[];
  probe_targets: ProviderLatencyProbeTarget[];
  active_targets?: ProviderLatencyProbeTarget[];
  cost_alerts: {
    enabled: boolean;
    daily_threshold_usd?: number | null;
    monthly_threshold_usd?: number | null;
  };
  last_run_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ProviderLatencyResult {
  id: string;
  provider: string;
  stage: string;
  success: boolean;
  skipped: boolean;
  total_ms?: number | null;
  ttfb_ms?: number | null;
  threshold_ms?: number | null;
  error_category?: string | null;
  error_message?: string | null;
  measured_at: string;
  details?: Record<string, any>;
}

export interface ProviderLatencyRunResponse {
  status: string;
  checked_targets: number;
  alert_count: number;
  results: ProviderLatencyResult[];
}

export const getProviderLatencyConfig = async (): Promise<ProviderLatencyMonitorConfig> => {
  const response = await api.get(PROVIDER_LATENCY_ENDPOINTS.CONFIG, {
    headers: getAdminAuthHeaders(),
  });
  return response.data as ProviderLatencyMonitorConfig;
};

export const updateProviderLatencyConfig = async (
  config: ProviderLatencyMonitorConfig
): Promise<ProviderLatencyMonitorConfig> => {
  const response = await api.put(PROVIDER_LATENCY_ENDPOINTS.CONFIG, config, {
    headers: getAdminAuthHeaders(),
  });
  return response.data as ProviderLatencyMonitorConfig;
};

export const getProviderLatencyResults = async (
  limit = 100
): Promise<ProviderLatencyResult[]> => {
  const response = await api.get(`${PROVIDER_LATENCY_ENDPOINTS.RESULTS}?limit=${limit}`, {
    headers: getAdminAuthHeaders(),
  });
  return response.data as ProviderLatencyResult[];
};

export const runProviderLatencyMonitorNow = async (): Promise<ProviderLatencyRunResponse> => {
  const response = await api.post(PROVIDER_LATENCY_ENDPOINTS.RUN_NOW, {}, {
    headers: getAdminAuthHeaders(),
  });
  return response.data as ProviderLatencyRunResponse;
};

export const getAgent = async (agentUuid: string): Promise<AgentDetail> => {
  try {
    const response = await api.get(ADMIN_AGENT_ENDPOINTS.GET(agentUuid), {
      headers: getAuthenticatedHeaders(),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getProviderManifest = async (): Promise<ProviderManifest> => {
  try {
    const token =
      localStorage.getItem("adminToken") || localStorage.getItem("token");
    const response = await api.get(ADMIN_AGENT_ENDPOINTS.PROVIDER_MANIFEST, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return response.data as ProviderManifest;
  } catch (error) {
    console.error("Error fetching provider manifest:", error);
    throw new Error(getErrorMessage(error));
  }
};

const getAdminAuthHeaders = () => {
  const token = localStorage.getItem("adminToken");
  if (!token) {
    throw new Error("Not authenticated as admin");
  }
  return { Authorization: `Bearer ${token}` };
};

const getAuthenticatedHeaders = () => {
  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");
  if (!token) {
    throw new Error("Not authenticated");
  }
  return { Authorization: `Bearer ${token}` };
};

export const getTestLabAgent = async (
  agentUuid: string
): Promise<AgentDetail> => {
  const response = await api.get(AGENT_EVALUATION_ENDPOINTS.AGENT(agentUuid), {
    headers: getAuthenticatedHeaders(),
  });
  return response.data as AgentDetail;
};

export const getTestLabProviderManifest = async (
  agentUuid: string
): Promise<ProviderManifest> => {
  const response = await api.get(
    AGENT_EVALUATION_ENDPOINTS.PROVIDER_MANIFEST(agentUuid),
    { headers: getAuthenticatedHeaders() }
  );
  return response.data as ProviderManifest;
};

export const getAgentEvaluationSuite = async (
  agentUuid: string
): Promise<AgentEvaluationSuite> => {
  try {
    const response = await api.get(AGENT_EVALUATION_ENDPOINTS.SUITE(agentUuid), {
      headers: getAuthenticatedHeaders(),
    });
    return response.data as AgentEvaluationSuite;
  } catch (error: any) {
    return throwAgentEvaluationError(error, { unavailableOn404: true });
  }
};

export const generateAgentEvaluationScenarios = async (
  agentUuid: string,
  request: AgentEvaluationGenerateScenariosRequest,
  signal?: AbortSignal
): Promise<AgentEvaluationGenerateScenariosResponse> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.GENERATE_SCENARIOS(agentUuid),
      request,
      {
        headers: getAuthenticatedHeaders(),
        signal,
      }
    );
    return response.data as AgentEvaluationGenerateScenariosResponse;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const createAgentEvaluationScenario = async (
  agentUuid: string,
  request: AgentEvaluationScenarioPayload
): Promise<AgentEvaluationScenario> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.SCENARIOS(agentUuid),
      request,
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as AgentEvaluationScenario;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const updateAgentEvaluationScenario = async (
  agentUuid: string,
  scenarioId: string,
  request: Partial<AgentEvaluationScenarioPayload>
): Promise<AgentEvaluationScenario> => {
  try {
    const response = await api.patch(
      AGENT_EVALUATION_ENDPOINTS.SCENARIO(agentUuid, scenarioId),
      request,
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as AgentEvaluationScenario;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const deleteAgentEvaluationScenario = async (
  agentUuid: string,
  scenarioId: string
): Promise<{ message: string }> => {
  try {
    const response = await api.delete(
      AGENT_EVALUATION_ENDPOINTS.SCENARIO(agentUuid, scenarioId),
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as { message: string };
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const duplicateAgentEvaluationScenario = async (
  agentUuid: string,
  scenarioId: string
): Promise<AgentEvaluationScenario> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.DUPLICATE_SCENARIO(agentUuid, scenarioId),
      {},
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as AgentEvaluationScenario;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const startAgentEvaluationRun = async (
  agentUuid: string,
  request: AgentEvaluationRunRequest,
  signal?: AbortSignal
): Promise<AgentEvaluationRun> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.RUNS(agentUuid),
      request,
      {
        headers: getAuthenticatedHeaders(),
        signal,
      }
    );
    return response.data as AgentEvaluationRun;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const getAgentEvaluationRun = async (
  agentUuid: string,
  runId: string
): Promise<AgentEvaluationRun> => {
  try {
    const response = await api.get(
      AGENT_EVALUATION_ENDPOINTS.RUN(agentUuid, runId),
      {
        headers: getAuthenticatedHeaders(),
      }
    );
    return response.data as AgentEvaluationRun;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const deleteAgentEvaluationRun = async (
  agentUuid: string,
  runId: string
): Promise<{ message: string }> => {
  try {
    const response = await api.delete(
      AGENT_EVALUATION_ENDPOINTS.RUN(agentUuid, runId),
      {
        headers: getAuthenticatedHeaders(),
      }
    );
    return response.data as { message: string };
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const createAgentPromptOptimization = async (
  agentUuid: string,
  request: AgentEvaluationPromptOptimizationRequest,
  signal?: AbortSignal
): Promise<AgentEvaluationPromptCandidate> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.PROMPT_OPTIMIZATIONS(agentUuid),
      request,
      { headers: getAuthenticatedHeaders(), signal }
    );
    return response.data as AgentEvaluationPromptCandidate;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const createManualAgentPromptCandidate = async (
  agentUuid: string,
  request: AgentEvaluationManualPromptCandidateRequest
): Promise<AgentEvaluationPromptCandidate> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.MANUAL_PROMPT_CANDIDATE(agentUuid),
      request,
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as AgentEvaluationPromptCandidate;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const updateAgentPromptCandidate = async (
  agentUuid: string,
  candidateId: string,
  request: AgentEvaluationPromptCandidateUpdate
): Promise<AgentEvaluationPromptCandidate> => {
  try {
    const response = await api.patch(
      AGENT_EVALUATION_ENDPOINTS.PROMPT_OPTIMIZATION(agentUuid, candidateId),
      request,
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as AgentEvaluationPromptCandidate;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const acceptAgentPromptCandidate = async (
  agentUuid: string,
  candidateId: string
): Promise<AgentPromptVersion> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.ACCEPT_PROMPT_OPTIMIZATION(agentUuid, candidateId),
      {},
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as AgentPromptVersion;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const rollbackAgentPromptVersion = async (
  agentUuid: string,
  versionId: string
): Promise<AgentPromptVersion> => {
  try {
    const response = await api.post(
      AGENT_EVALUATION_ENDPOINTS.ROLLBACK_PROMPT_VERSION(agentUuid, versionId),
      {},
      { headers: getAuthenticatedHeaders() }
    );
    return response.data as AgentPromptVersion;
  } catch (error: any) {
    return throwAgentEvaluationError(error);
  }
};

export const updateAgent = async (
  agentUuid: string,
  agentData: Partial<AgentCreate>
): Promise<AgentDetail> => {
  try {
    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.UPDATE(agentUuid),
      agentData,
      {
        headers: getAuthenticatedHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.delete(ADMIN_AGENT_ENDPOINTS.DELETE(agentUuid), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const enableAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.ENABLE(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const disableAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.DISABLE(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const enableIsPublicAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.ENABLE_IS_PUBLIC(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const disableIsPublicAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.DISABLE_IS_PUBLIC(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const enableCanDisconnectAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.ENABLE_CAN_DISCONNECT(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const disableCanDisconnectAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.DISABLE_CAN_DISCONNECT(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Re-export agent access functions
export const accessVoiceAgent = async (
  agentUuid: string
): Promise<{
  success: boolean;
  message?: string;
  remainingUses?: number;
  error?: string;
  agent_info?: AgentSummary;
}> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      return {
        success: false,
        error: "Not authenticated",
      };
    }

    const response = await api.post(
      AGENT_ENDPOINTS.ACCESS(agentUuid),
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return {
      success: true,
      remainingUses: response.data.remaining_uses,
      agent_info: response.data.agent_info,
    };
  } catch (error: any) {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      return {
        success: false,
        error: "Token is no longer valid. Please request a new one.",
      };
    }
    return {
      success: false,
      error: error.response?.data?.detail || "Failed to access voice agent",
    };
  }
};

// Audio Recordings interfaces
export interface AudioRecording {
  filename: string;
  file_id: string;
  file_size: number;
  created_at: string;
}

export interface AudioRecordingsResponse {
  items: AudioRecording[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

// Audio Recordings functions
export const fetchAudioRecordings = async (
  page = 1,
  pageSize = 20
): Promise<AudioRecordingsResponse> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.get(
      `${AUDIO_RECORDINGS_ENDPOINTS.LIST}?page=${page}&page_size=${pageSize}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch audio recordings:", error);
    throw new Error(getErrorMessage(error));
  }
};

export const downloadAudioFile = async (filename: string): Promise<Blob> => {
  try {
    // Try to get either admin token or token
    const adminToken = localStorage.getItem("adminToken");
    const token = adminToken || localStorage.getItem("token");

    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await api.get(
      AUDIO_RECORDINGS_ENDPOINTS.DOWNLOAD(filename),
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      }
    );

    return response.data;
  } catch (error: any) {
    console.error(`Failed to download audio file "${filename}":`, error);
    throw new Error(getErrorMessage(error));
  }
};

export const streamAudioFile = async (filename: string): Promise<Blob> => {
  try {
    const adminToken = localStorage.getItem("adminToken");
    const token = adminToken || localStorage.getItem("token");

    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await api.get(
      AUDIO_RECORDINGS_ENDPOINTS.STREAM(filename),
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      }
    );

    return response.data;
  } catch (error: any) {
    console.error(`Failed to stream audio file "${filename}":`, error);
    throw new Error(getErrorMessage(error));
  }
};

// Tag API Functions
export const fetchTags = async (): Promise<Tag[]> => {
  try {
    const response = await api.get(TAG_ENDPOINTS.LIST);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getTag = async (id: number): Promise<Tag> => {
  try {
    const response = await api.get(TAG_ENDPOINTS.GET(id));
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const createTag = async (tagData: {
  name: string;
  description: string;
}): Promise<Tag> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(TAG_ENDPOINTS.CREATE, tagData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateTag = async (
  id: number,
  tagData: { name: string; description: string }
): Promise<Tag> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(TAG_ENDPOINTS.UPDATE(id), tagData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteTag = async (id: number): Promise<void> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    await api.delete(TAG_ENDPOINTS.DELETE(id), {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Conversation API Functions
export const fetchConversations = async (
  limit = 10,
  offset = 0
): Promise<ConversationsResponse> => {
  try {
    const response = await api.get(
      `${CONVERSATIONS_ENDPOINTS.LIST}?limit=${limit}&offset=${offset}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteConversation = async (
  conversationId: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token =
      localStorage.getItem("adminToken") || localStorage.getItem("token");
    if (!token) {
      throw new Error("Not authenticated");
    }

    await api.delete(CONVERSATIONS_ENDPOINTS.DELETE(conversationId), {
      headers: { Authorization: `Bearer ${token}` },
    });

    return { success: true, message: "Conversation deleted successfully" };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

export const togglePinConversation = async (
  conversationId: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    await api.put(
      CONVERSATIONS_ENDPOINTS.TOGGLE_PIN(conversationId),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return { success: true, message: "Conversation pinned successfully" };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Export for backward compatibility
export default api;

export const createDisposableAgentLink = async (
  agentUuid: string
): Promise<{ url: string; message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(
      ADMIN_AGENT_ENDPOINTS.CREATE_DISPOSABLE(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    // The backend returns a data object containing the UUID
    const uuid = response.data.data || response.data;

    // Construct the frontend URL using the UUID
    const baseUrl = window.location.origin;
    const uploadUrl = `${baseUrl}/upload/${uuid}`;

    return {
      url: uploadUrl,
      message: "Disposable link created successfully",
    };
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const uploadAgentFiles = async (
  agentUuid: string,
  files: File[]
): Promise<{ success: boolean; message: string }> => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await api.post(
      AGENT_ENDPOINTS.UPLOAD(agentUuid),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return {
      success: true,
      message: response.data.message || "Files uploaded successfully",
    };
  } catch (error: any) {
    console.error("Error uploading files:", error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};

export const checkDisposableUuid = async (
  uuid: string
): Promise<{ valid: boolean; agentUuid?: string; message?: string }> => {
  try {
    // Make a GET request to check if the UUID exists
    const response = await api.get(AGENT_ENDPOINTS.UPLOAD(uuid));

    return {
      valid: true,
      agentUuid: response.data.agent_uuid,
      message: response.data.message || "UUID is valid",
    };
  } catch (error: any) {
    // If the server returns a 404 or other error, the UUID is invalid
    return {
      valid: false,
      message: getErrorMessage(error),
    };
  }
};

// Re-export Lead functions (renamed from OTP)
export const fetchLeadList = async (
  limit = 10,
  offset = 0
): Promise<LeadListResponse> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.get(
      `${LEAD_ENDPOINTS.LIST}?limit=${limit}&offset=${offset}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch Leads:", error);
    throw new Error(getErrorMessage(error));
  }
};

export const createLeads = async (
  count: number,
  maxUses: number
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  data?: Lead[];
}> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(
      LEAD_ENDPOINTS.CREATE,
      {
        count: count,
        max_uses: maxUses,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return {
      success: true,
      message: "Leads created successfully",
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

export const updateLead = async (
  leadId: string,
  maxUses: number
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      LEAD_ENDPOINTS.UPDATE(leadId),
      {
        max_uses: maxUses,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return {
      success: true,
      message: "Lead updated successfully",
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

export const deleteLead = async (
  leadId: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    await api.delete(LEAD_ENDPOINTS.DELETE(leadId), {
      headers: { Authorization: `Bearer ${token}` },
    });

    return {
      success: true,
      message: "Lead deleted successfully",
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

export const createUserLead = async (
  userData: LeadSignup
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  data?: Lead;
}> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(LEAD_ENDPOINTS.SIGNUP, userData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return {
      success: true,
      message: "User created successfully",
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Add enable/disable phone call functions after other agent functions
export const enablePhoneCallAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.ENABLE_PHONE_CALL(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const disablePhoneCallAgent = async (
  agentUuid: string
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.put(
      ADMIN_AGENT_ENDPOINTS.DISABLE_PHONE_CALL(agentUuid),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Phone call request
export interface PhoneCallRequest {
  phone: string;
  agent_uuid: string;
  fields?: AgentRuntimeFields;
  name?: string;
}

export interface ProviderDebug {
  source?: string;
  strategy?: string;
  actual_llm_provider?: string;
  providers?: Record<string, string>;
}

export interface DirectCallTraceEvent {
  sequence: number;
  type: string;
  stage?: string;
  provider?: string;
  event?: string;
  duration_ms?: number;
  from_event?: string;
  to_event?: string;
  started_at?: string | null;
  completed_at?: string | null;
  timeline_order?: number;
  timeline_label?: string;
  tool_name?: string;
  variables?: Record<string, string | number | boolean | null>;
  processor?: string;
  model?: string;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  cacheReadInputTokens?: number | null;
  promptCacheState?: string | null;
}

export interface DirectCallProviderProbeResult {
  provider: string;
  success: boolean;
  total_ms?: number | null;
  error?: string | null;
}

export interface DirectCallProviderComparison {
  current_provider?: string;
  selected_provider?: string;
  candidates?: string[];
  results?: DirectCallProviderProbeResult[];
}

export interface DirectCallProviderCheck {
  strategy?: string;
  providers?: Record<string, string>;
  selected_providers?: Record<string, string>;
  comparison_providers?: Record<string, string[] | string>;
  comparisons?: Record<string, DirectCallProviderComparison>;
}

// Add the phone call API function
export const initiatePhoneCall = async (
  phone: string,
  agentUuid: string,
  fields?: AgentRuntimeFields,
  name?: string
): Promise<{
  success: boolean;
  message: string;
  provider_debug?: ProviderDebug;
  provider_debug_session_id?: string;
}> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await api.post(
      AGENT_ENDPOINTS.PHONE_CALL,
      {
        phone: phone,
        agent_uuid: agentUuid,
        fields,
        name,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return {
      success: true,
      message: response.data?.message || "Phone call initiated successfully",
      provider_debug: response.data?.data?.provider_debug,
      provider_debug_session_id: response.data?.data?.provider_debug_session_id,
    };
  } catch (error: any) {
    console.error("Failed to initiate phone call:", error);
    return {
      success: false,
      message: getErrorMessage(error) || "Failed to initiate phone call",
    };
  }
};

export const checkPhoneCallProviders = async (
  phone: string,
  agentUuid: string,
  fields?: AgentRuntimeFields,
  name?: string
): Promise<{
  success: boolean;
  message: string;
  provider_check?: DirectCallProviderCheck;
}> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await api.post(
      AGENT_ENDPOINTS.PHONE_CALL_PROVIDER_CHECK,
      {
        phone,
        agent_uuid: agentUuid,
        fields,
        name,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return {
      success: true,
      message: response.data?.message || "Provider check completed",
      provider_check: response.data?.data,
    };
  } catch (error: any) {
    console.error("Failed to check phone call providers:", error);
    return {
      success: false,
      message: getErrorMessage(error) || "Failed to check providers",
    };
  }
};

export const getPhoneCallProviderDebug = async (
  sessionId: string
): Promise<{
  ready: boolean;
  provider_debug?: ProviderDebug;
  events?: DirectCallTraceEvent[];
}> => {
  const response = await api.get(
    `/agents/phone_call/provider-debug/${sessionId}`
  );
  return {
    ready: Boolean(response.data?.ready),
    provider_debug: response.data?.provider_debug,
    events: Array.isArray(response.data?.events) ? response.data.events : [],
  };
};

// Validate agent access token
export const validateAgentToken = async (
  accessToken: string
): Promise<{
  success: boolean;
  remainingUses?: number;
  agent_info?: AgentSummary;
  error?: string;
}> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      return {
        success: false,
        error: "Not authenticated",
      };
    }

    const response = await api.post(
      AGENT_ENDPOINTS.VALIDATE_TOKEN,
      {
        access_token: accessToken,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return {
      success: true,
      remainingUses: response.data.remaining_uses,
      agent_info: response.data.agent_info,
    };
  } catch (error: any) {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      return {
        success: false,
        error: "Token is no longer valid. Please request a new one.",
      };
    }
    return {
      success: false,
      error: error.response?.data?.detail || "Failed to validate access token",
    };
  }
};

export const fetchUnknownConversations = async (): Promise<Conversation[]> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.get(CONVERSATIONS_ENDPOINTS.UNKNOWN, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch unknown conversations:", error);
    throw new Error(getErrorMessage(error));
  }
};

// Get agents for a lead
export const getLeadAgents = async (
  leadId: string
): Promise<LeadAgentAccess[]> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.get(LEAD_ENDPOINTS.GET_AGENTS(leadId), {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to get lead agents:", error);
    throw new Error(getErrorMessage(error));
  }
};

// Add agent access to a lead
export const addAgentToLead = async (
  leadId: string,
  agentUuid: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.post(
      LEAD_ENDPOINTS.ADD_AGENT(leadId),
      { agent_uuid: agentUuid },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return {
      success: true,
      message: response.data.message || "Agent access added successfully",
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Remove agent access from a lead
export const removeAgentFromLead = async (
  leadId: string,
  agentUuid: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      throw new Error("Not authenticated as admin");
    }

    const response = await api.delete(
      LEAD_ENDPOINTS.REMOVE_AGENT(leadId, agentUuid),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return {
      success: true,
      message: response.data.message || "Agent access removed successfully",
    };
  } catch (error: any) {
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};

// Campaign interfaces
export enum CampaignStatus {
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  RUNNING = "running",
  PAUSED = "paused",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum CallStatus {
  PENDING = "pending",
  CALLING = "calling",
  COMPLETED = "completed",
  FAILED = "failed",
  SKIPPED = "skipped",
  RESCHEDULED = "rescheduled",
}

export interface Campaign {
  id: string;
  name: string;
  agent_id: string;
  status: CampaignStatus;
  total_contacts: number;
  processed_contacts: number;
  successful_calls: number;
  failed_calls: number;
  csv_file_url?: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  settings: Record<string, any>;
  success_metrics: SuccessMetric[];
  agent_name: string;
  daily_calls_made?: number;
  last_reset_date?: string;
  total_minutes?: number;
}

export interface CampaignContact {
  id: string;
  campaign_id: string;
  phone_number: string;
  first_name?: string;
  last_name?: string;
  call_status: CallStatus;
  call_attempts: number;
  last_called_at?: string;
  call_result?: string;
  created_at: string;
}

export interface CampaignCreate {
  name: string;
  agent_uuid: string;
  settings?: Record<string, any>;
  success_metrics: SuccessMetric[];
}

export interface CampaignUpdate {
  name?: string;
  agent_id?: string;
  status?: CampaignStatus;
  settings?: Record<string, any>;
  success_metrics?: SuccessMetric[];
}

export interface CampaignStats {
  campaign_id: string;
  total_contacts: number;
  processed_contacts: number;
  pending_contacts: number;
  completed_contacts: number;
  failed_calls: number;
  success_rate: number;
  active_calls: number;
  time_elapsed?: string;
}

export interface CampaignCallStats {
  calls_in_progress: number;
  queue_depth: number;
  success_rate: number;
  calls_per_hour: number;
  todays_calls: number;
  todays_target: number;
  hourly_breakdown: any[];
  outcome_distribution: Record<string, number>;
  average_call_duration: number;
  total_minutes_consumed: number;
}

export interface CampaignScheduleConfig {
  timezone?: string;
  calling_hours?: {
    start_time: string;
    end_time: string;
  };
  active_days?: string[];
  call_limits?: {
    max_per_day: number;
    max_concurrent: number;
  };
}

export interface CallQueueEntry {
  id: string;
  campaign_id: string;
  contact_id: string;
  scheduled_for: string;
  priority: number;
  status: string;
  task_id?: string;
  created_at: string;
  processed_at?: string;
}

export interface CallQueueResponse {
  queue_entries: CallQueueEntry[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface PauseCampaignResponse {
  campaign_id: string;
  tasks_cancelled: number;
  status: CampaignStatus;
  message: string;
}

export interface DashboardStats {
  active_campaigns: number;
  calls_today: number;
  success_rate: number;
  minutes_used: number;
  total_minutes: number;
}

export interface CampaignListResponse {
  campaigns: Campaign[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface CampaignContactsResponse {
  contacts: CampaignContact[];
  total: number;
  page_size: number;
  has_more: boolean;
  page: number;
}

// Helper function to get the appropriate token
const getCampaignToken = (): string => {
  const adminToken = localStorage.getItem("adminToken");
  const userToken = localStorage.getItem("token");

  // Prefer admin token if available, otherwise use user token
  if (adminToken) {
    return adminToken;
  } else if (userToken) {
    return userToken;
  } else {
    throw new Error("Not authenticated");
  }
};

// Campaign API functions
export const fetchCampaigns = async (
  page = 1,
  pageSize = 10,
  status?: CampaignStatus,
  search?: string
): Promise<CampaignListResponse> => {
  try {
    const token = getCampaignToken();

    const offset = (page - 1) * pageSize;
    let url = `${CAMPAIGN_ENDPOINTS.LIST}?limit=${pageSize}&offset=${offset}`;
    if (status) {
      url += `&status_filter=${status}`;
    }
    console.log(search);
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    const response = await api.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const createCampaign = async (
  campaignData: CampaignCreate
): Promise<Campaign> => {
  try {
    const token = getCampaignToken();

    const response = await api.post(CAMPAIGN_ENDPOINTS.CREATE, campaignData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getCampaign = async (id: string): Promise<Campaign> => {
  try {
    const token = getCampaignToken();

    const response = await api.get(CAMPAIGN_ENDPOINTS.GET(id), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateCampaign = async (
  id: string,
  campaignData: CampaignUpdate
): Promise<Campaign> => {
  try {
    const token = getCampaignToken();

    const response = await api.put(
      CAMPAIGN_ENDPOINTS.UPDATE(id),
      campaignData,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteCampaign = async (
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();

    await api.delete(CAMPAIGN_ENDPOINTS.DELETE(id), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { success: true, message: "Campaign deleted successfully" };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

export const getCampaigns = async (
  limit: number = 100,
  offset: number = 0,
  search?: string
): Promise<CampaignListResponse> => {
  try {
    const token = getCampaignToken();

    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    if (search && search.trim() !== "") {
      params.set("search", search.trim());
    }

    const response = await api.get(
      `${CAMPAIGN_ENDPOINTS.LIST}?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const uploadCampaignContacts = async (
  id: string,
  file: File
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();

    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post(
      CAMPAIGN_ENDPOINTS.UPLOAD_CONTACTS(id),
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

export const fetchCampaignContacts = async (
  id: string,
  page = 1,
  pageSize = 20,
  callStatus?: CallStatus,
  search?: string
): Promise<CampaignContactsResponse> => {
  try {
    const token = getCampaignToken();

    const offset = (page - 1) * pageSize;
    let url = `${CAMPAIGN_ENDPOINTS.CONTACTS(id)}?limit=${pageSize}&offset=${offset}`;
    if (callStatus) {
      url += `&call_status=${callStatus}`;
    }
    if (search && search.trim() !== "") {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }

    const response = await api.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const startCampaign = async (
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();

    const response = await api.post(
      CAMPAIGN_ENDPOINTS.START(id),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

export const pauseCampaign = async (
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();

    const response = await api.post(
      CAMPAIGN_ENDPOINTS.PAUSE(id),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

export const stopCampaign = async (
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();

    const response = await api.post(
      CAMPAIGN_ENDPOINTS.STOP(id),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

export const getCampaignStats = async (id: string): Promise<CampaignStats> => {
  try {
    const token = getCampaignToken();

    const response = await api.get(CAMPAIGN_ENDPOINTS.STATS(id), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const token = getCampaignToken();
    const response = await api.get(`${CAMPAIGN_ENDPOINTS.LIST}/dashboard/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// New API methods for real-time call monitoring
export const getCampaignCallStats = async (id: string): Promise<CampaignCallStats> => {
  try {
    const token = getCampaignToken();
    const response = await api.get(`${CAMPAIGN_ENDPOINTS.LIST}/${id}/call-stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateCampaignSchedule = async (
  id: string,
  config: CampaignScheduleConfig
): Promise<Campaign> => {
  try {
    const token = getCampaignToken();
    const response = await api.post(
      `${CAMPAIGN_ENDPOINTS.LIST}/${id}/schedule-config`,
      config,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getCampaignCallQueue = async (
  id: string,
  page = 1,
  pageSize = 20,
  status?: string
): Promise<CallQueueResponse> => {
  try {
    const token = getCampaignToken();
    const params = new URLSearchParams();
    params.append("limit", pageSize.toString());
    params.append("offset", ((page - 1) * pageSize).toString());
    if (status) params.append("status", status);

    const response = await api.get(
      `${CAMPAIGN_ENDPOINTS.LIST}/${id}/call-queue?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const pauseCampaignCalling = async (
  id: string
): Promise<PauseCampaignResponse> => {
  try {
    const token = getCampaignToken();
    const response = await api.post(
      `${CAMPAIGN_ENDPOINTS.LIST}/${id}/pause-calling`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const resumeCampaignCalling = async (
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();
    const response = await api.post(
      `${CAMPAIGN_ENDPOINTS.LIST}/${id}/resume-calling`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

// Campaign Lead Access Management
export const addLeadToCampaign = async (
  campaignId: string,
  leadId: number
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();
    const response = await api.post(
      CAMPAIGN_ENDPOINTS.ADD_LEAD(campaignId, leadId),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

export const removeLeadFromCampaign = async (
  campaignId: string,
  leadId: number
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const token = getCampaignToken();
    const response = await api.delete(
      CAMPAIGN_ENDPOINTS.REMOVE_LEAD(campaignId, leadId),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { success: true, message: response.data.message };
  } catch (error: any) {
    return { success: false, error: getErrorMessage(error) };
  }
};

export const getCampaignLeadAccess = async (
  campaignId: string
): Promise<{ lead_ids: number[] }> => {
  try {
    const token = getCampaignToken();
    const response = await api.get(
      CAMPAIGN_ENDPOINTS.GET_LEADS(campaignId),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Get Campaign Success Metrics Analytics
export const getCampaignSuccessMetricsAnalytics = async (
  campaignId: string,
  params?: {
    date_from?: string;
    date_to?: string;
    metric_priorities?: string;
    metric_categories?: string;
    call_status?: string;
    include_temporal?: boolean;
    min_confidence?: number;
  }
): Promise<CampaignAnalyticsResponse> => {
  try {
    const token = getCampaignToken();
    const response = await api.get(
      `${CAMPAIGN_ENDPOINTS.LIST}/${campaignId}/analytics`,
      {
        params,
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Export Campaign Analytics
export const exportCampaignAnalytics = async (
  campaignId: string,
  filters?: {
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    minDuration?: number;
    maxDuration?: number;
    callStatus?: string;
    outcome?: string;
    hideBlankMetrics?: boolean;
    email?: string;
  }
): Promise<{ success: boolean; message: string }> => {
  try {
    const token = getCampaignToken();
    const payload = filters
      ? {
          search: filters.search,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          min_duration: filters.minDuration,
          max_duration: filters.maxDuration,
          call_status: filters.callStatus,
          outcome: filters.outcome,
          hide_blank_metrics: filters.hideBlankMetrics,
          email: filters.email,
        }
      : {};
    const response = await api.post(
      `${CAMPAIGN_ENDPOINTS.LIST}/${campaignId}/analytics/export`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// Live Data Interfaces for campaign call logs table
export interface LiveDataFilters {
  dateFrom?: string;
  dateTo?: string;
  minDuration?: number;
  maxDuration?: number;
  callStatus?: string;
  outcome?: string;
  hideBlankMetrics?: boolean;
}

export interface LiveDataSortOptions {
  sortBy: string;
  sortOrder: "asc" | "desc";
}

export interface LiveCallLogEntry {
  call_log_id: number;
  phone_number: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
  call_status: string;
  call_attempts: number;
  call_outcome?: string;
  duration: number;
  recording_url?: string;
  transcript_url?: string;
  disconnected_by?: string;
  call_failure_insight?: string;
  analytics?: Record<string, any>;
  can_retry_analysis?: boolean;
  analysis_retry_ineligible_reason?: string;
}

export interface LiveCallLogsResponse {
  data: LiveCallLogEntry[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface LiveDataParams {
  limit?: number;
  offset?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  dateFrom?: string;
  dateTo?: string;
  minDuration?: number;
  maxDuration?: number;
  callStatus?: string;
  outcome?: string;
  hideBlankMetrics?: boolean;
}

/**
 * Get live call logs data with pagination, search, sort, and filter support
 * @param campaignId - Campaign UUID
 * @param params - Query parameters for pagination, search, sort, filter
 * @returns Promise with paginated call logs response
 */
export const getCampaignLiveData = async (
  campaignId: string,
  params: LiveDataParams = {}
): Promise<LiveCallLogsResponse> => {
  try {
    const queryParams = new URLSearchParams();

    // Add pagination params
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params.offset !== undefined) queryParams.append("offset", params.offset.toString());

    // Add search param
    if (params.search) queryParams.append("search", params.search);

    // Add sort params
    if (params.sortBy) queryParams.append("sort_by", params.sortBy);
    if (params.sortOrder) queryParams.append("sort_order", params.sortOrder);

    // Add filter params
    if (params.dateFrom) queryParams.append("date_from", params.dateFrom);
    if (params.dateTo) queryParams.append("date_to", params.dateTo);
    if (params.minDuration !== undefined) queryParams.append("min_duration", params.minDuration.toString());
    if (params.maxDuration !== undefined) queryParams.append("max_duration", params.maxDuration.toString());
    if (params.callStatus) queryParams.append("call_status", params.callStatus);
    if (params.outcome) queryParams.append("outcome", params.outcome);
    if (params.hideBlankMetrics !== undefined) {
      queryParams.append("hide_blank_metrics", String(params.hideBlankMetrics));
    }

    const response = await api.get<LiveCallLogsResponse>(
      `${CAMPAIGN_ENDPOINTS.LIST}/${campaignId}/analytics/live-data?${queryParams.toString()}`
    );

    return response.data;
  } catch (error: any) {
    console.error("Error fetching live call logs:", error);
    throw handleApiError(error);
  }
};

export const retryCallLogAnalysis = async (
  campaignId: string,
  callLogId: number
): Promise<{ success: boolean; message: string; data?: Record<string, any> }> => {
  try {
    const response = await api.post<{ success: boolean; message: string; data?: Record<string, any> }>(
      `${CAMPAIGN_ENDPOINTS.LIST}/${campaignId}/analytics/live-data/${callLogId}/retry-analysis`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error retrying call analysis:", error);
    throw handleApiError(error);
  }
};
